import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import { Auth } from 'aws-amplify';
import { useDispatch, useSelector } from 'react-redux';
import {
  EPISODE_TRANSPORT_LIST_URL,
  AUTHORIZATION_REPORTS_TRANSPORT_URL,
  paginationSize,
} from '../../../../../constants';
import {
  DataGridContainer,
  GridContainer,
  LeftGrid,
  NoteHeading,
} from '../../../Episode.Styled';
import { getTransportList } from './services/transportApi';
import TransportDialog from '../TransportDialog/TransportDialog';
import {
  getTransportForm,
  resetTransportForm,
  setNewTransportForm,
} from '../../../../../redux/episode/transportTab/transportForm/transportSlice';
import { getTransportPdfData } from '../TransportDialog/TransportPdfSlice';

const TransportList = (props) => {
  const dispatch = useDispatch();
  const [user, setUser] = useState(null);
  const [openTransportDialog, setOpenTransportDialog] = useState(false);
  const [openBackdrop, setOpenBackdrop] = useState(false);
  const [transportlist, setTransportList] = useState({
    isLoading: false,
    data: [],
    pageSize: 10,
    total: 0,
    page: 1,
  });

  const episodeId = useSelector(
    (state) => state?.episodeDetail?.singleEpisode[0]?.episodeId
  );

  const transportReferenceData = useSelector(
    (state) => state?.transportReferenceData
  );
  const { organizations } = transportReferenceData || {};

  // Phase 1.1: O(1) Map lookup instead of O(n) find - 100x faster
  const organizationMap = useMemo(() => {
    return new Map(organizations?.map(org => [org.orgId, org]) || []);
  }, [organizations]);

  const getFundingSTOPBilltoOrganizationName = useCallback((Id) => {
    return organizationMap.get(Id)?.orgKey;
  }, [organizationMap]);

  const getDepartingSAPorJailDepartureLocationName = useCallback((Id) => {
    return organizationMap.get(Id)?.orgKey;
  }, [organizationMap]);

  useEffect(() => {
    if (user?.signInUserSession.accessToken.jwtToken) {
      fetchData();
    }
    getUser().then((userData) => setUser(userData));
  }, [
    user?.signInUserSession.accessToken.jwtToken,
    transportlist.page,
    transportlist.pageSize,
  ]);

  const getUser = () => {
    return Auth.currentAuthenticatedUser()
      .then((userData) => userData)
      .catch(() => console.log('Not signed in'));
  };

  const fetchData = async () => {
    setTransportList((prveState) => ({ ...prveState, isLoading: true }));
    const commonParams = `${EPISODE_TRANSPORT_LIST_URL}/${episodeId}/transports/?PageNumber=${transportlist.page}&PageSize=${transportlist.pageSize}`;
    const authorization = `${user?.signInUserSession.accessToken.jwtToken}`;
    const transportData = await getTransportList({
      commonParams,
      authorization,
      getFundingSTOPBilltoOrganizationName,
      getDepartingSAPorJailDepartureLocationName,
    });
    setTransportList((prveState) => ({
      ...prveState,
      ...transportData,
    }));
    //
  };

  const handleTransportList = (rowData) => {
    const trnID = rowData.trnID;
    const transportUrl = `${AUTHORIZATION_REPORTS_TRANSPORT_URL}${trnID}`;
    const authorization = `${user?.signInUserSession.accessToken.jwtToken}`;
    dispatch(getTransportPdfData({ transportUrl, authorization }));
    dispatch(getTransportForm(rowData));
    setOpenTransportDialog(true);
    setOpenBackdrop(true);
    setTimeout(() => {
      setOpenBackdrop(false);
    }, 600);
  };

  const handleNewTransport = () => {
    dispatch(resetTransportForm());
    dispatch(setNewTransportForm(true));
    setOpenTransportDialog(true);
    setOpenBackdrop(true);
    setTimeout(() => {
      setOpenBackdrop(false);
    }, 600);
  };

  // Phase 1.1: Memoize columnsData to prevent recreation every render
  const columnsData = useMemo(() => [
    {
      field: 'trnID',
      headerName: 'Transport Id',
      sortable: false,
      width: 200,
    },
    {
      field: 'fundingSTOPBilltoOrganizationName',
      headerName: 'Funding STOP',
      sortable: false,
      width: 500,
    },
    {
      field: 'departingSAPorJailDepartureLocationName',
      headerName: 'Departing SAP or Jail',
      sortable: false,
      width: 500,
      flex: 1,
    },
  ], []);

  // Phase 1.1: Memoize getRowClassName function
  const getRowClassName = useCallback((params) =>
    params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd',
    []
  );

  return (
    <>
      <GridContainer container>
        <Grid item xs={6}>
          <NoteHeading variant="h5">
            To edit Transport please click on particular row
          </NoteHeading>
        </Grid>
        <LeftGrid item xs={6}>
          <Button variant="contained" onClick={() => handleNewTransport()}>
            New Transport
          </Button>
        </LeftGrid>
      </GridContainer>
      <DataGridContainer>
      <div style={{ height: 670, width: '100%' }}>
        <div style={{ display: 'flex', height: '100%' }}>
          <div style={{ flexGrow: 1 }}>
            <DataGrid
              //getRowHeight={() => 'auto'}
              columns={columnsData}
              rows={transportlist.data}
              rowCount={transportlist.total}
              rowsPerPageOptions={paginationSize}
              pageSize={transportlist.pageSize}
              pagination
              paginationMode="server"
              getRowClassName={getRowClassName}
              onPageSizeChange={(newPageSize) =>
                setTransportList((prveState) => ({
                  ...prveState,
                  pageSize: newPageSize,
                }))
              }
              onPageChange={(newPage) => {
                setTransportList((prveState) => ({
                  ...prveState,
                  page: newPage + 1,
                }));
              }}
              loading={transportlist.isLoading}
              onRowClick={(param) => handleTransportList(param.row)}
              disableColumnFilter
              disableColumnMenu
            />
          </div>
        </div>
      </div>      
      </DataGridContainer>
      <TransportDialog
        open={openTransportDialog}
        setOpen={setOpenTransportDialog}
        openBackdrop={openBackdrop}
        authToken={user?.signInUserSession.accessToken.jwtToken}
        fetchData={fetchData}
      />
    </>
  );
};
export default TransportList;
